// Phase 10 · Add Variant form. Admin provides Country, Env, Task, Goal
// and Scenario; clicks Generate to get GPT-5.6 K-R-R draft; reviews/edits;
// then saves as Draft (never auto-save, never auto-approve).
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, X } from "lucide-react";

const ISO_SAMPLE = [
  "Jordan","New Zealand","United Kingdom","United Arab Emirates",
  "Australia","India","Singapore","Kenya","Ireland","Canada",
];

// Phase 11.6 · per-primitive scenario_text template builder.
// Each builder returns a plain object with the shape its primitive
// expects. All strings are placeholders derived from Task / Goal /
// Scenario brief so the admin has a filled skeleton to edit, not an
// empty template. IDs are stable snake/kebab-case.
function buildScenarioTemplate(primitive, ctx) {
  const { taskText, goalText, briefText, task_brief } = ctx;
  switch (primitive) {
    case "iddl_browser":
      return {
        task_brief,
        research_question: goalText || taskText || "What question must the candidate answer from the available sources?",
        queries: {
          vague: [
            { id: "v_src_01", title: "Informal community mention",
              url: "community-wiki://topic", domain: "community-wiki.org",
              snippet: briefText || "Hearsay / anecdotal content with no citations.",
              credible: false, relevant: true,
              reason: "no author · no date · no citations" },
            { id: "v_src_02", title: "Second-hand summary",
              url: "blog://summary", domain: "blog.example",
              snippet: "Secondary summary lacking supporting evidence.",
              credible: false, relevant: true,
              reason: "no primary source · unverifiable" },
          ],
          refined: [
            { id: "r_src_01", title: "Primary archival record",
              url: "catalogue://primary-record-01", domain: "catalogue",
              snippet: briefText || "Authoritative institutional record with provenance chain.",
              credible: true, relevant: true,
              reason: "primary source · verified custody" },
            { id: "r_src_02", title: "Peer-reviewed secondary source",
              url: "journal://article-02", domain: "journal.example",
              snippet: "Peer-reviewed analysis citing primary records.",
              credible: true, relevant: true,
              reason: "cited sources · known author · dated" },
          ],
        },
        vague_query_hint:   "A broad keyword search returns too many unreliable results.",
        refined_query_hint: "A focused query targeting authoritative provenance records is more defensible.",
      };

    case "iddl_inbox":
      return {
        task_brief,
        inbox_name: "staff-inbox@organisation.example",
        messages: [
          { id: "msg-1", is_phishing: false,
            from_display: "Internal colleague",
            from_email:   "colleague@organisation.example",
            from_real_domain: "organisation.example",
            to: "staff-inbox@organisation.example",
            received_at: "2026-01-01T09:00:00Z",
            subject: "Routine internal request",
            body: briefText || "Legitimate day-to-day request with no red flags.",
            links: [], attachments: [], header_red_flags: [] },
          { id: "msg-2", is_phishing: false,
            from_display: "External partner",
            from_email:   "partner@partner.example",
            from_real_domain: "partner.example",
            to: "staff-inbox@organisation.example",
            received_at: "2026-01-01T09:15:00Z",
            subject: "Routine external enquiry",
            body: "Reasonable external enquiry from a known partner.",
            links: [], attachments: [], header_red_flags: [] },
          { id: "msg-3", is_phishing: true,
            from_display: "IT Support Desk",
            from_email:   "it-support@organisation-portal.net",
            from_real_domain: "organisation-portal.net",
            to: "staff-inbox@organisation.example",
            received_at: "2026-01-01T10:02:00Z",
            subject: "URGENT: credentials expire in 2 hours",
            body: "Credential-harvesting attempt. Click here to re-verify your password.",
            links: [
              { id: "lnk-3", display_text: "Click here to confirm your password",
                display_url: "https://portal.organisation.example/verify",
                real_url:    "https://organisation-portal.net/harvest" },
            ],
            attachments: [],
            header_red_flags: ["Display name does not match sending domain", "Reply-to mismatch"] },
          { id: "msg-4", is_phishing: false,
            from_display: "Known supplier",
            from_email:   "accounts@supplier.example",
            from_real_domain: "supplier.example",
            to: "staff-inbox@organisation.example",
            received_at: "2026-01-01T10:40:00Z",
            subject: "Monthly invoice",
            body: "Legitimate recurring invoice from an onboarded supplier.",
            links: [], attachments: [], header_red_flags: [] },
        ],
      };

    case "iddl_ai_chat":
      return {
        task_brief,
        source_material_title: "Source material to summarise",
        source_material: briefText || "Full source text the candidate must summarise or transform using the AI assistant.",
        audience_hint: "the intended audience (e.g. a lay reader, a board, a new hire)",
        format_hint: "the required output format (e.g. ≤ 5 bullet points, 150 words)",
        weak_output_template: "Draft output (weak): unverified attributions, no citations, generic tone.",
        strong_output_template: "Draft output (refined): cites confirmed sources, matches audience + format, avoids unsupported claims.",
        vague_signals: ["summary", "describe", "tell me about"],
        refinement_signals: ["bullet", "audience", "limit", "words", "concise", "format"],
      };

    case "iddl_file_manager":
      return {
        task_brief,
        folders: [
          { id: "inbox",      label: "Inbox",      kind: "system" },
          { id: "restricted", label: "Restricted", kind: "secure" },
          { id: "public",     label: "Public",     kind: "shared" },
        ],
        external_destinations: [
          { id: "email",         label: "Email attachment", risk: "high"   },
          { id: "public_drive",  label: "Public drive",     risk: "medium" },
        ],
        files: [
          { id: "f_01",
            name: "sensitive_record.xlsx",
            owner: "Owner Team",
            initial_folder: "inbox",
            content: briefText || "Contains sensitive personal / financial data.",
            correct_classification: "restricted",
            correct_destination_folder: "restricted",
            correct_renamed_to: "sensitive_record__restricted.xlsx",
            needs_classification: true,
            needs_relocation: true,
            needs_rename: true },
          { id: "f_02",
            name: "public_notice.pdf",
            owner: "Comms",
            initial_folder: "inbox",
            content: "Public-facing notice — safe to share widely.",
            correct_classification: "public",
            correct_destination_folder: "public",
            correct_renamed_to: "public_notice.pdf",
            needs_classification: true,
            needs_relocation: true,
            needs_rename: false },
          { id: "f_03",
            name: "internal_memo.docx",
            owner: "Owner Team",
            initial_folder: "inbox",
            content: "Internal-only memo; must not leave the organisation.",
            correct_classification: "restricted",
            correct_destination_folder: "restricted",
            correct_renamed_to: "internal_memo__restricted.docx",
            needs_classification: true,
            needs_relocation: true,
            needs_rename: true },
        ],
      };

    case "iddl_settings":
      return {
        task_brief,
        sections: [
          { id: "account",  label: "Account",  icon: "user",   description: "Account identity and sign-in." },
          { id: "privacy",  label: "Privacy",  icon: "shield", description: "Who can see your activity and data." },
          { id: "security", label: "Security", icon: "lock",   description: "Protections on your account." },
        ],
        settings: [
          { id: "mfa",
            section_id: "security",
            label: "Two-factor authentication",
            description: "Require a second factor on every sign-in.",
            control_type: "toggle",
            options: [ { value: "on", label: "On" }, { value: "off", label: "Off" } ],
            initial_value: "off",
            correct_value: "on",
            unsafe_values: ["off"],
            risk_level: "high",
            must_be_secure: true,
            warning_text: briefText || "MFA is required for any account with access to sensitive data." },
          { id: "profile_visibility",
            section_id: "privacy",
            label: "Profile visibility",
            description: "Who can see your profile.",
            control_type: "radio",
            options: [
              { value: "public",  label: "Public" },
              { value: "contacts",label: "Contacts only" },
              { value: "private", label: "Private" },
            ],
            initial_value: "public",
            correct_value: "contacts",
            unsafe_values: ["public"],
            risk_level: "medium",
            must_be_secure: false,
            warning_text: "A public profile exposes activity to anyone on the internet." },
          { id: "password_rotation",
            section_id: "security",
            label: "Password rotation",
            description: "How often the password must be changed.",
            control_type: "select",
            options: [
              { value: "never",     label: "Never" },
              { value: "quarterly", label: "Every 90 days" },
              { value: "annual",    label: "Every 12 months" },
            ],
            initial_value: "never",
            correct_value: "quarterly",
            unsafe_values: ["never"],
            risk_level: "medium",
            must_be_secure: true,
            warning_text: "Passwords that never rotate accumulate exposure risk over time." },
        ],
      };

    case "iddl_workflow":
      return {
        task_brief,
        alerts: [
          { id: "a_01", label: "Unusual admin login — geo mismatch", is_incident: true,  must_be_flagged: true,
            context: briefText || "Admin account signed in from an unexpected country at 03:14; MFA bypassed." },
          { id: "a_02", label: "Daily backup CPU spike",             is_incident: false, must_be_flagged: false,
            context: "Routine CPU spike during the scheduled backup window." },
          { id: "a_03", label: "Data-exfil pattern on finance share", is_incident: true,  must_be_flagged: true,
            context: "Large outbound transfer from the finance share to an external endpoint." },
        ],
        actions: [
          { id: "act_isolate", label: "Isolate affected host",   kind: "containment", must_be_done: true  },
          { id: "act_notify",  label: "Notify SecOps lead",      kind: "escalation",  must_be_done: true  },
          { id: "act_log",     label: "Record in incident log",  kind: "documentation", must_be_done: true  },
          { id: "act_ignore",  label: "Mark as noise",           kind: "dismiss",     must_be_done: false },
        ],
        channels: [
          { id: "ch_secure_bridge", label: "SecOps bridge (encrypted)", safe: true  },
          { id: "ch_company_chat",  label: "Company chat #general",     safe: false },
          { id: "ch_personal_sms",  label: "Personal SMS",              safe: false },
        ],
      };

    case "iddl_editor":
      return {
        task_brief,
        original_message: null,
        initial_draft: {
          to:      "counterparty@partner.example",
          cc:      "manager@organisation.example",
          subject: "RE: delivery slipped — need your side of the story NOW",
          body:    briefText || "This is UNACCEPTABLE. We expect a full explanation IMMEDIATELY.",
        },
        tone_signals: [
          { id: "sig_capsrun",     label: "ALL CAPS run",       kind: "shout",
            min_run: 3, must_be_absent: true,
            warning_text: "Runs of capitals read as shouting; soften before sending." },
          { id: "sig_hostile",     label: "Hostile wording",    kind: "tone",
            min_run: 1, must_be_absent: true,
            warning_text: "Hostile wording escalates the dispute instead of resolving it." },
          { id: "sig_secure_send", label: "Use secure channel", kind: "channel",
            min_run: 0, must_be_absent: false,
            warning_text: "This message discusses contractual terms and must go via the secure channel." },
        ],
      };

    case "iddl_ai_evaluate":
      return {
        task_brief,
        ai_output: {
          title: "Draft briefing produced by the AI assistant",
          body:  briefText || "The AI produced a short briefing on the topic the candidate must evaluate.",
          claims: [
            { id: "c_01", statement: "Claim fully supported by a cited primary source.",
              supported_by: ["s_primary"], uncertain: false },
            { id: "c_02", statement: "Claim with no source and no caveat.",
              supported_by: [], uncertain: true },
            { id: "c_03", statement: "Claim partially supported by a weak source.",
              supported_by: ["s_weak"],    uncertain: true },
          ],
          sources: [
            { id: "s_primary", label: "Primary archival record", note: "Verified custody; cited directly." },
            { id: "s_weak",    label: "Community wiki entry",    note: "Hearsay; no citations."           },
          ],
        },
      };

    case "iddl_ai_decision":
      return {
        task_brief,
        context: briefText || "The AI tool has proposed an action. Decide whether to accept it, require review, or reject it.",
        options: [
          { id: "o_accept",        label: "Accept the AI's recommendation as-is",
            details: "Act on the AI output directly without additional checks.",
            unsafe_direct_use: true  },
          { id: "o_sample_review", label: "Spot-check a sample, then approve the rest",
            details: "Validate a representative subset before approving at scale.",
            unsafe_direct_use: false },
          { id: "o_reject_human",  label: "Reject — require a human review per case",
            details: "Do not use the AI output in bulk; escalate each case for a human decision.",
            unsafe_direct_use: false },
        ],
      };

    case "iddl_ai_chat_agent":
      return {
        task_brief,
        agent_profile: {
          name:  "Task Assistant",
          role:  goalText || "research helper",
          tools: ["search", "read_document", "write_note"],
        },
        scripted_agent_run: [
          { step: 1, action: "search",
            summary: "Searched the available sources; 2 weak hits, 0 primary." },
          { step: 2, action: "read_document",
            summary: "Opened a weak source without inspecting its provenance." },
          { step: 3, action: "write_note",
            summary: briefText || "Drafted a note that cites claims WITHOUT a confirmed primary source." },
        ],
        agent_deliverable: {
          title: "Draft output produced by the agent",
          flag_review_of: [1, 3],
        },
      };

    case "iddl_ai_evaluate_agent":
      return {
        task_brief,
        agent_profile: {
          name:  "Research Agent",
          role:  goalText || "fact-finder",
          tools: ["web_search", "pdf_read"],
        },
        scripted_agent_run: [
          { step: 1, tool: "web_search", query: "topic overview",
            result: "Community wiki hit, no citations.",
            supports_claims: [] },
          { step: 2, tool: "pdf_read",   query: "primary record, section 1",
            result: "Primary source confirms claim c_01.",
            supports_claims: ["c_01"] },
          { step: 3, tool: "web_search", query: "additional context",
            result: briefText || "Secondary blog post; no primary source referenced.",
            supports_claims: [] },
        ],
        ai_output: {
          title: "Agent briefing (post-run)",
          body:  "The agent produced the following briefing from its tool trail.",
          claims: [
            { id: "c_01", statement: "Claim supported by a tool-trail primary source.",
              supported_by: ["s_primary"], fabricated: false },
            { id: "c_02", statement: "Claim with no supporting tool step — likely fabricated.",
              supported_by: [], fabricated: true },
          ],
          required_trail_steps: [ { id: "c_01" } ],
        },
      };

    case "iddl_ai_decision_agent":
      return {
        task_brief,
        context: briefText || "An autonomous agent has proposed a bulk action. Decide whether to approve, approve a sample first, or reject it.",
        human_authority_required: true,
        agent_proposal: {
          action: "bulk_action",
          count: 1000,
          reasoning: goalText || "The agent believes this reverses an earlier incorrect run.",
          reversibility: "low — once executed, the action cannot be undone",
          sign_off: null,
        },
        options: [
          { id: "o_approve",         label: "Approve the agent's proposal as-is",
            details: "Execute the bulk action without further review." },
          { id: "o_approve_sample",  label: "Approve a 10% sample first, then the remainder",
            details: "Validate the agent's proposal on a sample before full execution." },
          { id: "o_reject_per_case", label: "Reject — require per-case human review",
            details: "Do not execute in bulk; escalate each case for a human decision." },
        ],
      };

    default:
      // Unknown or empty primitive — emit a minimal shell the admin
      // can edit into whatever the current primitive expects.
      return { task_brief, candidate_task: taskText, candidate_goal: goalText };
  }
}

export default function VariantForm({ preset, onCancel, onSaved }) {
  const [concepts, setConcepts] = useState([]);     // {concept_id, concept_name, environments:[]}
  const [cid, setCid] = useState(preset?.concept_id || "");
  const [env, setEnv] = useState(preset?.environment ?? null);
  const [primitive, setPrimitive] = useState(preset?.primitive || "");
  const [variantId, setVariantId] = useState("");   // server assigns on save; preview label only
  const [cname, setCname]   = useState(preset?.concept_name || "");

  const [country, setCountry] = useState("");
  const [task, setTask] = useState("");
  const [goal, setGoal] = useState("");
  const [brief, setBrief] = useState("");
  const [scenarioJson, setScenarioJson] = useState("{}");

  const [draft, setDraft] = useState(null);  // {knowledge, reflection, remediation_message, ...}
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [variantName, setVariantName] = useState("");

  // Pre-populate the concept list from the current variants (which guarantees
  // we only let the admin pick a concept + env that actually has a canonical).
  useEffect(() => {
    (async () => {
      const r = await api.get("/admin/variants");
      const seen = {};
      for (const v of (r.data.variants || [])) {
        if (!v.is_default) continue;
        seen[v.concept_id] = seen[v.concept_id] || { concept_id: v.concept_id, concept_name: v.concept_name, subdomain: v.subdomain, envs: {} };
        const key = v.environment ?? "null";
        seen[v.concept_id].envs[key] = { env: v.environment, primitive: v.primitive };
      }
      setConcepts(Object.values(seen).sort((a,b) => a.concept_id.localeCompare(b.concept_id)));
      if (preset?.concept_id) {
        setCid(preset.concept_id);
        setEnv(preset.environment ?? null);
        setPrimitive(preset.primitive || "");
        setCname(preset.concept_name || "");
      }
    })();
  }, [preset]);

  // Keep primitive + cname in sync when the admin changes concept/env from
  // inside the form (when launched from the global "Add Variant" button).
  useEffect(() => {
    if (!cid || !concepts.length) return;
    const c = concepts.find(x => x.concept_id === cid);
    if (!c) return;
    setCname(c.concept_name);
    const key = env ?? "null";
    if (c.envs[key]) {
      setPrimitive(c.envs[key].primitive);
    } else {
      // Default to first available env for this concept
      const firstKey = Object.keys(c.envs)[0];
      setEnv(c.envs[firstKey].env);
      setPrimitive(c.envs[firstKey].primitive);
    }
  }, [cid, env, concepts]);

  // Phase 11 · suggest a default name once (when the field is still
  // empty). Never overwrite an admin-entered name — country and
  // variant_name are independent fields.
  const autoName = useMemo(() => {
    if (!cid || !cname) return "";
    const c = (country || "—").trim();
    return `${cid} ${cname} — ${c} — v1`;
  }, [cid, cname, country]);
  useEffect(() => {
    if (!variantName && autoName) setVariantName(autoName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoName]);

  const generate = async () => {
    try {
      JSON.parse(scenarioJson || "{}");
    } catch (e) {
      toast.error("Scenario JSON is invalid"); return;
    }
    setGenerating(true);
    try {
      const scenario_text = { ...JSON.parse(scenarioJson || "{}") };
      if (brief) scenario_text.task_brief = brief;
      const r = await api.post("/admin/variants/draft_krcr", {
        concept_id: cid, environment: env,
        country, candidate_task: task, candidate_goal: goal,
        scenario_text,
      });
      setDraft(r.data);
      setVariantId(r.data.variant_id);
      toast.success("Draft generated — review and edit before saving.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft generation failed");
    } finally { setGenerating(false); }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const scenario_text = { ...JSON.parse(scenarioJson || "{}") };
      if (brief) scenario_text.task_brief = brief;
      const payload = {
        concept_id: cid, environment: env,
        country, variant_name: variantName,
        candidate_task: task, candidate_goal: goal,
        scenario_text,
        knowledge: draft.knowledge,
        reflection: draft.reflection,
        remediation_message: draft.remediation_message,
        model_used: draft.model_used, generation_seed: draft.generation_seed,
      };
      const r = await api.post("/admin/variants", payload);
      toast.success(`Variant ${r.data.variant_id} saved as DRAFT`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const canGenerate = cid && country && task && goal && scenarioJson;

  // Phase 11.6 · emit a scenario_text JSON whose shape matches the
  // primitive bound to the current concept/env. Pure client-side —
  // no backend call, no schema change. Placeholder values are
  // seeded from Task / Goal / Scenario brief; the admin edits
  // freely before saving. Rerunning the button overwrites the
  // textarea with a fresh template.
  const generateScenarioJson = () => {
    const briefText = (brief || "").trim();
    const taskText  = (task  || "").trim();
    const goalText  = (goal  || "").trim();
    if (!briefText && !taskText && !goalText) {
      toast.error("Fill in at least one of Task, Goal or Scenario brief first.");
      return;
    }
    const task_brief = briefText || taskText || goalText;
    const tmpl = buildScenarioTemplate(primitive, { taskText, goalText, briefText, task_brief });
    setScenarioJson(JSON.stringify(tmpl, null, 2));
    toast.success("Scenario JSON generated — review and edit before saving.");
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-6 overflow-auto" data-testid="variant-form-modal">
      <div className="bg-white w-full max-w-5xl shadow-xl">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-[var(--iddl-rule)]">
          <h2 className="font-display font-semibold">Add Variant</h2>
          <code className="label-mono text-xs text-[var(--iddl-muted)]">{variantId || "VAR-… (assigned on save)"}</code>
          <button type="button" className="ms-auto text-sm" onClick={onCancel} data-testid="btn-close-variant-form"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* System-populated */}
          <section className="grid md:grid-cols-2 gap-3 bg-[var(--iddl-surface)] p-3">
            <div><label className="label-mono text-[11px]">Concept</label>
              <select value={cid} onChange={e => setCid(e.target.value)} disabled={!!preset?.concept_id} className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-concept">
                <option value="">— pick a concept —</option>
                {concepts.map(c => <option key={c.concept_id} value={c.concept_id}>{c.concept_id} · {c.concept_name}</option>)}
              </select>
            </div>
            <div><label className="label-mono text-[11px]">Environment</label>
              <select value={env ?? "null"} onChange={e => setEnv(e.target.value === "null" ? null : e.target.value)} className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-env">
                {cid && concepts.find(c => c.concept_id === cid) && Object.keys(concepts.find(c => c.concept_id === cid).envs).map(ek => (
                  <option key={ek} value={ek}>{ek === "agentic_ai" ? "Agentic AI" : ek === "ai" ? "AI" : "—"}</option>
                ))}
              </select>
            </div>
            <div><label className="label-mono text-[11px]">Primitive (auto)</label>
              <input readOnly value={primitive} className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm bg-white" data-testid="var-primitive"/>
            </div>
            <div><label className="label-mono text-[11px]">Variant name</label>
              <input value={variantName} onChange={e => setVariantName(e.target.value)} className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-name"/>
            </div>
          </section>

          {/* Admin-entered */}
          <section className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div><label className="label-mono text-[11px]">Country (contextual)</label>
                <input list="iso-countries" value={country} onChange={e => setCountry(e.target.value)}
                  className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" placeholder="e.g. Jordan, New Zealand"
                  data-testid="var-country"/>
                <datalist id="iso-countries">{ISO_SAMPLE.map(c => <option key={c} value={c}/>)}</datalist>
              </div>
            </div>
            <div><label className="label-mono text-[11px]">Task (candidate_task)</label>
              <textarea value={task} onChange={e => setTask(e.target.value)} rows={2} className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-task"/>
            </div>
            <div><label className="label-mono text-[11px]">Goal (candidate_goal)</label>
              <textarea value={goal} onChange={e => setGoal(e.target.value)} rows={2} className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-goal"/>
            </div>
            <div><label className="label-mono text-[11px]">Scenario brief (goes into scenario_text.task_brief)</label>
              <textarea value={brief} onChange={e => setBrief(e.target.value)} rows={2} className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-brief"/>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label-mono text-[11px]">Full scenario JSON (shape matches primitive)</label>
                <button type="button" onClick={generateScenarioJson}
                  className="btn-ink px-2.5 py-1 text-xs inline-flex items-center gap-1.5"
                  data-testid="btn-generate-scenario-json">
                  <Sparkles size={12}/> Generate Scenario JSON
                </button>
              </div>
              <textarea value={scenarioJson} onChange={e => setScenarioJson(e.target.value)} rows={6} className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-xs font-mono" data-testid="var-scenario-json"/>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" disabled={!canGenerate || generating} onClick={generate} className="btn-ink px-3 py-1.5 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="btn-generate-draft">
                {generating ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>} Generate K-C-R draft
              </button>
              <span className="text-xs text-[var(--iddl-muted)]">Capability contract is cloned from the canonical (concept, env) and locked.</span>
            </div>
          </section>

          {/* Draft review */}
          {draft && (
            <section className="space-y-4 border-t border-[var(--iddl-rule)] pt-4" data-testid="var-draft-review">
              <div className="paper p-3">
                <h3 className="font-display font-semibold mb-2">Knowledge (review before save)</h3>
                <label className="label-mono text-[11px]">Question</label>
                <textarea value={draft.knowledge.question_text} rows={2}
                  onChange={e => setDraft({...draft, knowledge: {...draft.knowledge, question_text: e.target.value}})}
                  className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-k-question"/>
                <div className="mt-2 space-y-1">
                  {(draft.knowledge.options || []).map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="radio" name="correct_idx" checked={draft.knowledge.correct_index === i}
                        onChange={() => setDraft({...draft, knowledge: {...draft.knowledge, correct_index: i}})}
                        data-testid={`var-k-correct-${i}`}/>
                      <input value={o}
                        onChange={e => {
                          const next = [...draft.knowledge.options]; next[i] = e.target.value;
                          setDraft({...draft, knowledge: {...draft.knowledge, options: next}});
                        }}
                        className="flex-1 border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid={`var-k-option-${i}`}/>
                    </div>
                  ))}
                </div>
                <label className="label-mono text-[11px] mt-2 inline-block">Rationale (audit-only; never shown to candidate)</label>
                <textarea value={draft.knowledge.rationale} rows={2}
                  onChange={e => setDraft({...draft, knowledge: {...draft.knowledge, rationale: e.target.value}})}
                  className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-k-rationale"/>
              </div>

              <div className="paper p-3">
                <h3 className="font-display font-semibold mb-2">Capability (locked clone of canonical)</h3>
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div><strong>Primitive:</strong> <code>{draft.primitive}</code></div>
                  <div><strong>Required events:</strong> {(draft.capability.required_events || []).length}</div>
                  <div><strong>Critical merit events:</strong> {(draft.capability.critical_merit_events || []).length}</div>
                  <div><strong>Auto-fail rules:</strong> {(draft.capability.auto_fail_rules || []).length}</div>
                  <div><strong>Valid final decisions:</strong> {(draft.capability.valid_final_decisions || []).join(", ") || "—"}</div>
                </div>
                <p className="text-xs text-[var(--iddl-muted)] mt-2">The behavioural contract cannot be edited through the Scenario Bank.</p>
              </div>

              <div className="paper p-3">
                <h3 className="font-display font-semibold mb-2">Reflection (locked labels, editable synonyms)</h3>
                <label className="label-mono text-[11px]">Prompt</label>
                <textarea value={draft.reflection.prompt} rows={2}
                  onChange={e => setDraft({...draft, reflection: {...draft.reflection, prompt: e.target.value}})}
                  className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-r-prompt"/>
                <div className="mt-2 space-y-2">
                  {(draft.reflection.themes || []).map((t, i) => (
                    <div key={i}>
                      <div className="text-xs"><strong>{t.label}</strong> (label locked)</div>
                      <input value={(t.any || []).join(", ")}
                        onChange={e => {
                          const next = [...draft.reflection.themes];
                          next[i] = {...t, any: e.target.value.split(/\s*,\s*/).filter(Boolean)};
                          setDraft({...draft, reflection: {...draft.reflection, themes: next}});
                        }}
                        className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-xs" data-testid={`var-r-theme-${i}`}/>
                    </div>
                  ))}
                </div>
              </div>

              <div className="paper p-3">
                <h3 className="font-display font-semibold mb-2">Remediation (candidate-facing)</h3>
                <textarea value={draft.remediation_message} rows={3}
                  onChange={e => setDraft({...draft, remediation_message: e.target.value})}
                  className="w-full border border-[var(--iddl-rule)] px-2 py-1 text-sm" data-testid="var-remediation"/>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm border border-[var(--iddl-rule)]">Cancel</button>
                <button type="button" onClick={save} disabled={saving} className="btn-ink px-3 py-1.5 text-sm inline-flex items-center gap-2" data-testid="btn-save-variant">
                  {saving ? <Loader2 className="animate-spin" size={14}/> : null} Save as Draft
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
